import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Upload as UploadIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { DashboardCard } from '../types';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const { 
    data: cards = [], 
    isLoading: cardsLoading
  } = useQuery<DashboardCard[]>({
    queryKey: ['dashboardCards'],
    queryFn: apiService.getDashboardCards,
    retry: 2,
    retryDelay: 1000,
  });

  const handleCardClick = (route: string) => {
    navigate(route);
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'upload':
        return <UploadIcon />;
      case 'assessment':
        return <AssessmentIcon />;
      case 'settings':
        return <SettingsIcon />;
      case 'trending_up':
        return <TrendingUpIcon />;
      default:
        return <DashboardIcon />;
    }
  };

  // Función para mostrar tarjetas por defecto cuando hay error
  const getDefaultCards = (): DashboardCard[] => [
    {
      id: 1,
      title: "Importar Tope para descuentos",
      description: "Sube archivos Excel con datos para topes de descuentos para procesar y analizar la información. Soporta formatos .xlsx y .xls",
      icon: "upload",
      route: "/excel-upload",
      color: "#FF6B00",
      isActive: true,
      order: 1
    },
    {
      id: 2,
      title: "Gestionar Topes de Descuento",
      description: "Administra los topes de descuento por cliente, rubro y producto. Configura la jerarquía de prioridades del sistema.",
      icon: "trending_up",
      route: "/topes-descuento",
      color: "#4CAF50",
      isActive: true,
      order: 2
    }
  ];

  // Asegurar que siempre tengamos datos válidos
  const displayCards = !cards.length ? getDefaultCards() : cards;

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary">
        Dashboard
      </Typography>

      {cardsLoading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Cargando funcionalidades...</Typography>
        </Box>
      )}

      {!cardsLoading && displayCards.length > 0 && (
        <Grid container spacing={3}>
          {displayCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8,
                  },
                  border: '2px solid',
                  borderColor: 'primary.main',
                  height: '100%',
                }}
                onClick={() => handleCardClick(card.route)}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: card.color,
                        borderRadius: '50%',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 3,
                        color: '#ffffff',
                      }}
                    >
                      {getIcon(card.icon)}
                    </Box>
                  </Box>
                  <Typography variant="h5" gutterBottom color="primary">
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {card.description}
                  </Typography>
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={getIcon(card.icon)}
                    sx={{ 
                      px: 3, 
                      py: 1,
                      fontSize: '1rem',
                      borderRadius: 2,
                    }}
                  >
                    {card.icon === 'upload' ? 'Comenzar Importación' : 'Gestionar Topes'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard; 